import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Coffee, CheckCircle2, Loader2, ArrowRight, Phone, MessageSquareText,
  Download, MailOpen, XCircle, Linkedin,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const PHONE_DISPLAY = "214-500-4192";
const PHONE_TEL = "+12145004192";
const GUIDE_URL = "/files/after-shift-reset.pdf";

const IMG = {
  hero: "https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcmVzdGluZyUyMGFmdGVyJTIwc2hpZnQlMjBraXRjaGVuJTIwd2FybSUyMGV2ZW5pbmd8ZW58MHx8fHwxNzkyMDAxOTQ5fDA&ixlib=rb-4.1.0&q=85",
  quiet: "https://images.unsplash.com/photo-1780147342156-e7b4d360474f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxxdWlldCUyMHJlc3RhdXJhbnQlMjBkaW5pbmclMjByb29tJTIwZW1wdHklMjBtb3JuaW5nJTIwc3VuJTIwd2FybXxlbnwwfHx8fDE3OTIwMDE5NDl9MA&ixlib=rb-4.1.0&q=85",
  tables: "https://images.unsplash.com/photo-1777709816320-b49fe8653d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxxdWlldCUyMHJlc3RhdXJhbnQlMjBkaW5pbmclMjByb29tJTIwZW1wdHklMjBtb3JuaW5nJTIwc3VuJTIwd2FybXxlbnwwfHx8fDE3OTIwMDE5NDl9MA&ixlib=rb-4.1.0&q=85",
};

const QUESTIONS = [
  { n: "01", q: "What is still running in my head from tonight?", note: "Brain dump. Naming it is what cages it." },
  { n: "02", q: "Does this need me — or does it just want me?", note: "If it can survive twelve hours, it belongs on tomorrow's list." },
  { n: "03", q: "What am I afraid will happen if I don't answer until morning?", note: "Most fears shrink the moment they're written down." },
  { n: "04", q: "What did I carry today that my team could learn to carry?", note: "One thing. That's tomorrow's delegation — and your future nights off." },
  { n: "05", q: "What is one thing tonight that belongs only to me?", note: "Twenty minutes, non-negotiable. Write it before the night eats it." },
];

const HOOKS = [
  "Fourteen-hour days on concrete floors",
  "The 2 AM adrenaline hit when you're trying to sleep",
  "Your phone on your chest in case a line cook calls out",
  "Missing family dinner while feeding two hundred strangers",
];

const reveal = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: EASE } }),
};

const scrollToForm = () => {
  document.querySelector("#reset-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

function Header() {
  return (
    <header className="border-b border-[#E2D9CC]" data-testid="reset-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#C85A32] text-[#FDFBF7] flex items-center justify-center">
            <Coffee size={17} />
          </span>
          <span className="leading-none">
            <span className="font-serif-accent font-semibold text-[#231F20] text-base block">John Jones</span>
            <span className="font-mono-accent text-[9px] uppercase tracking-[0.22em] text-[#6E6259] block mt-1">Thrive Beyond Service</span>
          </span>
        </div>
        <button data-testid="header-get-guide-btn" onClick={scrollToForm}
          className="inline-flex items-center gap-1.5 bg-[#C85A32] hover:bg-[#B04B25] hover:text-white text-white text-sm font-semibold px-4 sm:px-5 py-2.5 rounded-full transition-colors duration-200 whitespace-nowrap">
          <span className="hidden sm:inline">Get the free guide</span><span className="sm:hidden">Free guide</span> <ArrowRight size={15} />
        </button>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="py-16 md:py-24" data-testid="reset-hero">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={0}
            className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-5">
            For restaurant managers &amp; chefs
          </motion.p>
          <motion.h1 variants={reveal} initial="hidden" animate="show" custom={1}
            className="font-serif-accent font-semibold tracking-tight text-4xl sm:text-5xl lg:text-[3.4rem] text-[#231F20] leading-[1.15] mb-6"
            data-testid="hero-headline">
            Thrive <span className="italic text-[#C85A32]">Beyond</span> Service.
          </motion.h1>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={2}
            className="text-base sm:text-lg text-[#443E3C] leading-relaxed mb-8 max-w-xl" data-testid="hero-subheadline">
            You don't have to leave the restaurant to get your life back. <em>The After-Shift Reset</em> is a free
            one-page guide for managers and chefs caught in the 14-hour shift loop: five simple questions to
            quiet the 2 AM adrenaline, stop sleeping with your phone, and finally leave work at work.
          </motion.p>
          <motion.div variants={reveal} initial="hidden" animate="show" custom={3} className="flex flex-wrap items-center gap-4">
            <button data-testid="hero-download-guide-cta" onClick={scrollToForm}
              className="group inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B04B25] hover:text-white text-white font-semibold px-7 py-4 rounded-full transition-colors duration-200">
              Download The After-Shift Reset — free
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <span className="text-xs text-[#6E6259]">One page. Ten minutes a shift. Yours.</span>
          </motion.div>
        </div>
        <motion.div variants={reveal} initial="hidden" animate="show" custom={2} className="relative">
          <div className="rounded-[2rem] overflow-hidden border border-[#E2D9CC] shadow-xl shadow-[#C85A32]/10">
            <img src={IMG.hero} alt="A chef taking a quiet moment after service" className="w-full h-full object-cover" data-testid="hero-image" />
          </div>
          <p className="font-serif-accent italic text-[#6E6259] text-sm mt-4 text-center">The kitchen will be there tomorrow. Your evening shouldn't have to be.</p>
        </motion.div>
      </div>
    </section>
  );
}

function Hooks() {
  return (
    <section className="py-16 md:py-24 bg-[#F5EFE6]" data-testid="hooks-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-4">Sound familiar?</p>
          <h2 className="font-serif-accent font-medium tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#231F20] max-w-2xl leading-[1.2] mb-12">
            Nobody promotes you to GM and hands you a manual for your own brain.
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5">
          {HOOKS.map((h, i) => (
            <motion.div key={h} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i}
              className="rounded-2xl border border-[#E2D9CC] bg-[#FAF7F2] p-6 flex items-start gap-4" data-testid={`hook-card-${i}`}>
              <span className="font-mono-accent text-[#C85A32] text-sm font-semibold mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-[#443E3C] leading-relaxed">{h}.</p>
            </motion.div>
          ))}
        </div>
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
          className="font-serif-accent italic text-lg text-[#6E6259] mt-10 max-w-2xl">
          If you nodded at more than one of these, this page was written for you.
        </motion.p>
      </div>
    </section>
  );
}

function WhoFor() {
  const forList = [
    "GMs, AGMs, executive chefs, sous chefs, and F&B directors",
    "People who love this industry — and are losing themselves to it",
    "Anyone whose family gets the leftovers of their energy",
  ];
  const notList = [
    "A job pitch or a side hustle — the guide is free, and the coaching options below are completely optional",
    "A lecture about self-care from someone who's never worked a Friday double",
    "A promise to fix burnout — it's a reflection tool, not a cure",
  ];
  return (
    <section className="py-16 md:py-24" data-testid="who-for-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mb-12">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-4">Straight talk</p>
          <h2 className="font-serif-accent font-medium tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#231F20] max-w-2xl leading-[1.2]">
            Built for kitchen leaders and floor managers. Nothing more, nothing less.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-5">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={0}
            className="rounded-2xl border border-[#E2D9CC] bg-[#FAF7F2] p-8" data-testid="who-for-yes">
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#4A5D4E] font-semibold mb-6">This is for you if you're…</p>
            <ul className="space-y-4">
              {forList.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-[#443E3C] leading-relaxed">
                  <CheckCircle2 size={17} className="text-[#4A5D4E] mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={1}
            className="rounded-2xl border border-[#E2D9CC] bg-[#FAF7F2] p-8" data-testid="who-for-no">
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#6E6259] font-semibold mb-6">And to be clear, this is not…</p>
            <ul className="space-y-4">
              {notList.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-[#6E6259] leading-relaxed">
                  <XCircle size={17} className="text-[#6E6259]/60 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function GuidePreview() {
  return (
    <section className="py-16 md:py-24 bg-[#F5EFE6]" data-testid="guide-preview-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative order-2 lg:order-1">
          <div className="rounded-[2rem] overflow-hidden border border-[#E2D9CC]">
            <img src={IMG.quiet} alt="An empty, quiet dining room in warm morning light" className="w-full h-full object-cover" />
          </div>
          <p className="font-serif-accent italic text-[#6E6259] text-sm mt-4 text-center">The goal: more mornings and evenings that look like this.</p>
        </motion.div>
        <div className="order-1 lg:order-2">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-4">Inside the guide</p>
            <h2 className="font-serif-accent font-medium tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#231F20] leading-[1.2] mb-10">
              Five questions. Pen and paper. Ten minutes after your shift.
            </h2>
          </motion.div>
          <div className="space-y-4">
            {QUESTIONS.map(({ n, q, note }, i) => (
              <motion.div key={n} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}
                className="flex items-start gap-4" data-testid={`question-preview-${i}`}>
                <span className="font-mono-accent text-[#C85A32] text-sm font-semibold mt-1 shrink-0">{n}</span>
                <div>
                  <p className="font-serif-accent font-medium text-[#231F20] leading-snug">{q}</p>
                  <p className="text-sm text-[#6E6259] mt-1">{note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const PROGRAMS = [
  {
    name: "Do-It-Yourself System",
    price: "$97",
    per: "/month",
    tag: "At your own pace",
    slug: "diy",
    ctaCompact: "Start",
    desc: "The complete Life Mastery System, worked at your own pace — the full map, without the 1-on-1 price tag.",
    bullets: [
      "Complete step-by-step Life Mastery System",
      "Self-paced lessons, worksheets & templates",
      "Goal-clarity exercises & weekly action planners",
      "New tools and lessons added regularly",
    ],
    ctaLabel: "Start at $97/mo",
    ctaHref: "https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=P-4AC202602F133935XNLIG2PI",
  },
  {
    name: "Group Coaching Roundtable",
    slug: "group",
    price: "$497",
    per: "/month",
    tag: "Most popular · live weekly",
    featured: true,
    ctaCompact: "Join",
    desc: "Everything in the DIY System, plus a seat at a weekly one-hour live roundtable with John and other restaurant leaders — one focused discussion topic each week.",
    bullets: [
      "The complete Life Mastery System, included",
      "One-hour live group session with John every week",
      "Roundtable format — a new discussion topic each week",
      "Learn from managers & chefs fighting the same fight",
      "Cancel anytime",
    ],
    ctaLabel: "Join — $497/mo",
    ctaHref: "https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=P-4M95308494672261TNLKP4ZI",
  },
  {
    name: "One-on-One Coaching",
    slug: "coaching",
    ctaCompact: "Enroll",
    price: "$1,997",
    per: " · 12-week program",
    tag: "The fastest path",
    desc: "Private, personalized coaching for restaurant leaders ready to move fast, go deep, and build their next chapter with direct support.",
    bullets: [
      "Private coaching sessions directly with John",
      "A fully personalized mastery plan for your life",
      "Direct access to John between sessions",
      "Priority support & faster feedback loops",
    ],
    ctaLabel: "Enroll now — $1,997",
    ctaHref: "https://www.paypal.com/ncp/payment/FGFZNR6KKXGKG",
  },
];

function ProgramCards({ compact = false, testidPrefix = "program" }) {
  return (
    <div className={compact ? "space-y-3" : "grid md:grid-cols-2 lg:grid-cols-3 gap-5"}>
      {PROGRAMS.map((p) => (
        <div key={p.name}
          className={`rounded-2xl border p-6 ${p.featured ? "border-[#C85A32] bg-white shadow-lg shadow-[#C85A32]/10" : "border-[#E2D9CC] bg-[#FAF7F2]"} ${compact ? "flex items-center justify-between gap-4" : ""}`}
          data-testid={`${testidPrefix}-card-${p.slug}`}>
          <div className={compact ? "min-w-0" : ""}>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#C85A32] font-semibold mb-1.5">{p.tag}</p>
            <p className={`font-serif-accent font-semibold text-[#231F20] ${compact ? "text-base" : "text-xl mb-1"}`}>
              {p.name} <span className="text-[#C85A32]">{p.price}</span>
              <span className="text-sm font-normal text-[#6E6259]">{p.per}</span>
            </p>
            {!compact && <p className="text-sm text-[#6E6259] leading-relaxed mb-5">{p.desc}</p>}
            {!compact && (
              <ul className="space-y-2.5 mb-6">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-[#443E3C]">
                    <CheckCircle2 size={15} className="text-[#4A5D4E] mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            )}
            {!compact && (
              <a data-testid={`${testidPrefix}-cta-${p.slug}`} href={p.ctaHref}
                target={p.ctaHref.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                className={`group inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200 ${
                  p.featured
                    ? "bg-[#C85A32] hover:bg-[#B04B25] hover:text-white text-white"
                    : "border border-[#E2D9CC] text-[#443E3C] hover:border-[#C85A32] hover:text-[#C85A32]"
                }`}>
                {p.ctaLabel} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            )}
            {compact && <p className="text-xs text-[#6E6259] mt-1 leading-relaxed">{p.desc}</p>}
          </div>
          {compact && (
            <a data-testid={`${testidPrefix}-cta-${p.slug}`} href={p.ctaHref}
              target={p.ctaHref.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
              className="shrink-0 inline-flex items-center gap-1 bg-[#C85A32] text-white text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap">
              {p.ctaCompact} <ArrowRight size={12} />
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function AboutJohn() {
  return (
    <section className="py-16 md:py-24 bg-[#231F20] text-[#FDFBF7]" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-10 lg:gap-14 items-center">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-xs mx-auto lg:mx-0 w-full">
          <div className="rounded-[2rem] bg-[#F5EFE6] p-5 rotate-[-2deg] shadow-2xl shadow-black/40">
            <img src="/files/john-jones.png" alt="John Jones, LifeMastery coach" className="w-full h-auto rounded-3xl" data-testid="about-photo" />
          </div>
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C85A32] font-semibold text-center mt-5">40 years in restaurants &amp; hotels</p>
        </motion.div>
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={1}>
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-4">About your coach</p>
          <h2 className="font-serif-accent font-medium tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-[1.2] mb-6">Hi, I'm John.</h2>
          <div className="space-y-4 text-[#C9BFB4] text-sm sm:text-base leading-relaxed" data-testid="about-copy">
            <p>I've spent 40 years in restaurants and hotels, and I'm still an active restaurant manager today. I've run the Friday-night rush, worked every holiday, and eaten dinner standing up at 9:45 more times than I can count.</p>
            <p>I love this industry. I also know how easy it is for the job to take over the rest of your life, because I've watched it happen to great people around me.</p>
            <p>Now, as a certified LifeWorks Life Mastery coach, I help restaurant managers and chefs design a life of their dreams outside the restaurant.</p>
          </div>
          <p className="font-serif-accent italic text-[#FDFBF7] text-base sm:text-lg mt-6 leading-relaxed">
            "No lectures. No self-care fluff from someone who's never worked a double. Just what 40 years in the business taught me, in a way you can use this week."
          </p>
          <a href="https://www.linkedin.com/in/joinjohnjones" target="_blank" rel="noreferrer" data-testid="about-linkedin-link"
            className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#C85A32] hover:text-[#E06B3A] transition-colors">
            <Linkedin size={16} /> Connect with me on LinkedIn
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function CoachingSection() {
  return (
    <section id="coaching" className="py-16 md:py-24" data-testid="coaching-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mb-12">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-4">When you're ready for more</p>
          <h2 className="font-serif-accent font-medium tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#231F20] max-w-2xl leading-[1.2] mb-4">
            The guide is step one. LifeMastery is the path.
          </h2>
          <p className="text-[#443E3C] max-w-2xl leading-relaxed">
            Three ways in — all built for people still working full-throttle in this industry.
            No schedulers, no pressure: call or text John directly and he'll tell you honestly which fits, if either.
          </p>
        </motion.div>
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
          <ProgramCards />
        </motion.div>
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
          className="flex flex-wrap gap-3 mt-8">
          <a data-testid="coaching-call-btn" href={`tel:${PHONE_TEL}`}
            className="inline-flex items-center gap-1.5 bg-[#C85A32] hover:bg-[#B04B25] hover:text-white text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200">
            <Phone size={14} /> Call {PHONE_DISPLAY}
          </a>
          <a data-testid="coaching-text-btn" href={`sms:${PHONE_TEL}`}
            className="inline-flex items-center gap-1.5 border border-[#E2D9CC] text-[#443E3C] hover:border-[#C85A32] hover:text-[#C85A32] text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200">
            <MessageSquareText size={14} /> Text John
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function SignupForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: "lifeworks",
        message: "After-Shift Reset guide request",
        consent: true,
      });
      setDone(true);
      toast.success("Guide on its way — check your inbox.");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#E2D9CC] bg-white px-4 py-3.5 text-[#231F20] placeholder:text-[#6E6259]/60 focus:outline-none focus:ring-2 focus:ring-[#C85A32]/25 focus:border-[#C85A32] transition-colors duration-200";

  return (
    <section id="reset-form" className="py-16 md:py-24 scroll-mt-6" data-testid="form-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="rounded-[2.5rem] border border-[#E2D9CC] bg-[#FAF7F2] overflow-hidden grid lg:grid-cols-2">
          <div className="relative min-h-[280px]">
            <img src={IMG.tables} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#231F20]/70 via-[#231F20]/20 to-transparent" />
            <p className="absolute bottom-8 left-8 right-8 font-serif-accent italic text-xl sm:text-2xl text-[#FDFBF7] leading-snug">
              "Take care of the person who takes care of the restaurant."
            </p>
          </div>
          <div className="p-9 md:p-14">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}
                  className="h-full min-h-[340px] flex flex-col items-center justify-center text-center" data-testid="thank-you-state">
                  <span className="w-16 h-16 rounded-full bg-[#4A5D4E]/15 text-[#4A5D4E] flex items-center justify-center mb-6">
                    <MailOpen size={30} />
                  </span>
                  <h3 className="font-serif-accent font-semibold text-2xl sm:text-3xl text-[#231F20] mb-3">
                    Check your inbox, {form.name.split(" ")[0]}.
                  </h3>
                  <p className="text-[#6E6259] max-w-sm leading-relaxed mb-8">
                    The After-Shift Reset is on its way to {form.email}. Use it tonight —
                    pen and paper, after your shift. Or grab it right now:
                  </p>
                  <a data-testid="thank-you-direct-download-btn" href={GUIDE_URL} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B04B25] hover:text-white text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200 mb-8">
                    <Download size={16} /> Download the guide now
                  </a>
                  <div className="w-full border-t border-[#E2D9CC] pt-6 mb-6 text-left">
                    <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#C85A32] font-semibold mb-1 text-center">The guide is step one</p>
                    <p className="text-xs text-[#6E6259] text-center mb-4">When you're ready for the full path — three ways in:</p>
                    <ProgramCards compact testidPrefix="thankyou-program" />
                  </div>
                  <div className="border-t border-[#E2D9CC] pt-6 w-full">
                    <p className="text-sm text-[#6E6259] mb-4">Questions about the guide or the programs? I answer my own phone.</p>
                    <div className="flex justify-center gap-3">
                      <a data-testid="thank-you-call-btn" href={`tel:${PHONE_TEL}`}
                        className="inline-flex items-center gap-1.5 border border-[#E2D9CC] text-[#443E3C] hover:border-[#C85A32] hover:text-[#C85A32] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">
                        <Phone size={14} /> Call John
                      </a>
                      <a data-testid="thank-you-text-btn" href={`sms:${PHONE_TEL}`}
                        className="inline-flex items-center gap-1.5 border border-[#E2D9CC] text-[#443E3C] hover:border-[#C85A32] hover:text-[#C85A32] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">
                        <MessageSquareText size={14} /> Text John
                      </a>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.form key="form" exit={{ opacity: 0 }} onSubmit={submit} className="space-y-4" data-testid="signup-form">
                  <h3 className="font-serif-accent font-semibold text-2xl sm:text-3xl text-[#231F20] mb-1">
                    Get The After-Shift Reset — free.
                  </h3>
                  <p className="text-sm text-[#6E6259] mb-6">
                    One page, delivered to your inbox in about a minute. No cost, no pitch attached.
                  </p>
                  <input data-testid="signup-name-input" required minLength={2} placeholder="First and last name" value={form.name} onChange={set("name")} className={inputCls} />
                  <input data-testid="signup-email-input" required type="email" placeholder="Email address (where the guide goes)" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="signup-phone-input" minLength={7} type="tel" placeholder="Cell phone (optional — if you'd like a text)" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <label className="flex items-start gap-3 text-[11px] text-[#6E6259] leading-relaxed cursor-pointer select-none" data-testid="signup-consent-label">
                    <input data-testid="signup-consent-checkbox" required type="checkbox" className="mt-0.5 w-4 h-4 shrink-0 rounded border-[#E2D9CC] accent-[#C85A32]" />
                    <span>I agree John Jones may send me the guide and follow up about it by email and text. Reply STOP to opt out of texts anytime. We never ask for passwords or card details.</span>
                  </label>
                  <button data-testid="signup-submit-btn" type="submit" disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#C85A32] hover:bg-[#B04B25] hover:text-white disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-colors duration-200">
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                    {submitting ? "Sending..." : "Email me the free guide"}
                  </button>
                  <p className="text-[11px] text-[#6E6259] text-center">No spam, no list-selling. Unsubscribe anytime.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#E2D9CC] py-10" data-testid="reset-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
        <p className="text-sm text-[#6E6259]">
          <span className="font-serif-accent font-semibold text-[#231F20]">Thrive Beyond Service</span> · by John Jones, someone who has lived the restaurant life ·{" "}
          <a href="https://www.linkedin.com/in/joinjohnjones" target="_blank" rel="noreferrer" className="text-[#C85A32] font-semibold hover:underline" data-testid="footer-linkedin-link">LinkedIn</a>
        </p>
        <p className="text-sm text-[#6E6259]">
          Questions? Call or text <a href={`tel:${PHONE_TEL}`} className="text-[#C85A32] font-semibold hover:underline" data-testid="footer-phone-link">{PHONE_DISPLAY}</a>
        </p>
      </div>
    </footer>
  );
}

export default function ResetPage() {
  useEffect(() => {
    document.title = "Thrive Beyond Service — The After-Shift Reset for restaurant managers & chefs";
    if (sessionStorage.getItem("lw_tracked")) return;
    sessionStorage.setItem("lw_tracked", "1");
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: "/reset", referrer: document.referrer, event: "visit" }),
    }).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#443E3C] overflow-x-clip" data-testid="reset-page">
      <Header />
      <Hero />
      <Hooks />
      <WhoFor />
      <GuidePreview />
      <SignupForm />
      <AboutJohn />
      <CoachingSection />
      <Footer />
    </main>
  );
}
